import React, { useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SortIcon from '@mui/icons-material/Sort';
import TuneIcon from '@mui/icons-material/Tune';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import {
  CrmButton,
  CrmPopover,
  CrmInput,
  CrmSelect,
  CrmSearchInput,
  CrmTooltip,
} from '../ui';
import { formatDate } from '../../utils/dateFormatter';
import { useSettings } from '../../context/SettingsContext';
import CrmDateFilterInput from './CrmDateFilterInput';

const getFilterSummary = (filter, definition, dateFormat = 'DD/MM/YYYY') => {
  if (!filter || !definition) return '';
  if (definition.type === 'date-range') {
    const start = formatDate(filter.startDate, dateFormat);
    const end = formatDate(filter.endDate, dateFormat);
    if (start && end) return `${start} to ${end}`;
    return start ? `From ${start}` : `Until ${end}`;
  }

  if (Array.isArray(filter.values)) return `${filter.values.length} selected`;
  const option = definition.options?.find((item) => String(item.value) === String(filter.value));
  return option?.label || filter.value || '';
};

function CrmFilterPopover({ definition, value, onApply, onClear, triggerClassName = '' }) {
  const { settings } = useSettings();
  const dateFormat = settings?.date_format || 'DD/MM/YYYY';
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || {});

  useEffect(() => {
    if (open) setDraft(value || {});
  }, [open, value]);

  const active = Boolean(
    value?.value !== undefined && value?.value !== '' ||
    value?.startDate ||
    value?.endDate ||
    value?.values?.length
  );

  const apply = () => {
    onApply(draft);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={`crm-list-filter-trigger ${active ? 'is-active' : ''} ${triggerClassName}`.trim()}
        onClick={() => setOpen((previous) => !previous)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span>{definition.label}</span>
        {active ? <span className="crm-list-filter-trigger__count">1</span> : null}
        <KeyboardArrowDownIcon aria-hidden="true" />
      </button>

      <CrmPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        align="start"
        role="dialog"
        className="crm-list-filter-popover"
      >
        <div className="crm-list-popover__title">{definition.label}</div>

        {definition.type === 'date-range' ? (
          <div className="crm-list-filter-popover__fields">
            <label>
              <span>From</span>
              <CrmDateFilterInput
                value={draft.startDate || ''}
                dateFormat={dateFormat}
                ariaLabel={`${definition.label} from`}
                onChange={(startDate) => setDraft((previous) => ({ ...previous, startDate }))}
              />
            </label>
            <label>
              <span>To</span>
              <CrmDateFilterInput
                value={draft.endDate || ''}
                dateFormat={dateFormat}
                ariaLabel={`${definition.label} to`}
                onChange={(endDate) => setDraft((previous) => ({ ...previous, endDate }))}
              />
            </label>
          </div>
        ) : null}

        {definition.type === 'select' ? (
          <div className="crm-list-filter-options" role="listbox" aria-label={definition.label}>
            {definition.options?.map((option) => (
              <button
                key={String(option.value)}
                type="button"
                role="option"
                aria-selected={String(draft.value ?? '') === String(option.value)}
                className={String(draft.value ?? '') === String(option.value) ? 'is-selected' : ''}
                onClick={() => setDraft({ value: option.value })}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}

        {definition.type === 'text' ? (
          <CrmInput
            type="text"
            value={draft.value || ''}
            placeholder={`Filter ${definition.label.toLowerCase()}`}
            onChange={(event) => setDraft({ value: event.target.value })}
          />
        ) : null}

        <div className="crm-list-popover__actions">
          <CrmButton
            size="small"
            variant="tertiary"
            onClick={() => {
              onClear();
              setOpen(false);
            }}
          >
            Clear
          </CrmButton>
          <CrmButton size="small" variant="primary" onClick={apply}>Apply</CrmButton>
        </div>
      </CrmPopover>
    </>
  );
}

function CrmSortControl({ columns, sort, onSortChange }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const sortableColumns = columns.filter((column) => column.sortable !== false);
  const selectedId = sort?.columnId || sortableColumns[0]?.id || '';
  const selectedColumn = sortableColumns.find((column) => column.id === selectedId);
  const directionLabels = selectedColumn?.type === 'date'
    ? { desc: 'Newest first', asc: 'Oldest first' }
    : selectedColumn?.type === 'number' || selectedColumn?.type === 'currency'
      ? { desc: 'Highest first', asc: 'Lowest first' }
      : selectedColumn?.type === 'boolean'
        ? { desc: 'Yes first', asc: 'No first' }
        : { desc: 'Z to A', asc: 'A to Z' };

  return (
    <>
      <CrmButton
        ref={anchorRef}
        size="small"
        startIcon={<SortIcon />}
        onClick={() => setOpen((previous) => !previous)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        Sort by
      </CrmButton>
      <CrmPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        align="start"
        role="dialog"
        className="crm-list-sort-popover"
      >
        <div className="crm-list-popover__title">Sort by</div>
        <CrmSelect
          value={selectedId}
          onChange={(event) => onSortChange({
            columnId: event.target.value,
            direction: sort?.direction || 'asc',
          })}
          aria-label="Sort property"
        >
          {sortableColumns.map((column) => (
            <option key={column.id} value={column.id}>{column.label}</option>
          ))}
        </CrmSelect>
        <div className="crm-list-sort-directions" role="group" aria-label="Sort direction">
          <button
            type="button"
            className={sort?.direction === 'desc' ? 'is-active' : ''}
            onClick={() => onSortChange({ columnId: selectedId, direction: 'desc' })}
          >
            {directionLabels.desc}
          </button>
          <button
            type="button"
            className={sort?.direction !== 'desc' ? 'is-active' : ''}
            onClick={() => onSortChange({ columnId: selectedId, direction: 'asc' })}
          >
            {directionLabels.asc}
          </button>
        </div>
      </CrmPopover>
    </>
  );
}

function CrmListToolbar({
  search,
  onSearchChange,
  columns,
  sort,
  onSortChange,
  filterDefinitions,
  filters,
  onFilterChange,
  onClearFilter,
  activeFilters,
  onClearFilters,
  onAdvancedFilters,
  onEditColumns,
  filtersVisible,
  onToggleFilters,
  chromeCollapsed,
  onToggleChrome,
  searchPlaceholder = 'Search records',
}) {
  const { settings } = useSettings();
  const dateFormat = settings?.date_format || 'DD/MM/YYYY';
  const quickFilters = useMemo(
    () => filterDefinitions.filter((definition) => definition.quick),
    [filterDefinitions]
  );

  return (
    <div className="crm-list-toolbar">
      <div className="crm-list-toolbar__primary">
        <div className="crm-list-search-wrap">
          <CrmSearchInput
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />
          {search ? (
            <button
              type="button"
              className="crm-list-search-clear"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
            >
              <CloseIcon />
            </button>
          ) : null}
        </div>

        <CrmButton
          size="small"
          className={filtersVisible ? 'crm-list-filter-button is-active' : 'crm-list-filter-button'}
          startIcon={<FilterListIcon />}
          onClick={onToggleFilters}
          aria-pressed={filtersVisible}
        >
          Filter
        </CrmButton>
        <CrmSortControl columns={columns} sort={sort} onSortChange={onSortChange} />

        <div className="crm-list-toolbar__spacer" />
        <CrmTooltip content="Edit columns" placement="bottom">
          <CrmButton
            iconOnly
            size="small"
            variant="tertiary"
            onClick={onEditColumns}
            aria-label="Edit columns"
          >
            <ViewColumnOutlinedIcon />
          </CrmButton>
        </CrmTooltip>
        <CrmTooltip content={chromeCollapsed ? 'Show page header and views' : 'Hide page header and views'} placement="bottom">
          <CrmButton
            iconOnly
            size="small"
            variant="tertiary"
            onClick={onToggleChrome}
            aria-label={chromeCollapsed ? 'Show page header and views' : 'Hide page header and views'}
            aria-pressed={chromeCollapsed}
          >
            {chromeCollapsed ? <KeyboardArrowDownOutlinedIcon /> : <KeyboardArrowUpOutlinedIcon />}
          </CrmButton>
        </CrmTooltip>
      </div>

      {filtersVisible ? (
        <div className="crm-list-toolbar__filters">
          {quickFilters.map((definition) => (
            <CrmFilterPopover
              key={definition.id}
              definition={definition}
              value={filters[definition.id]}
              onApply={(value) => onFilterChange(definition.id, value)}
              onClear={() => onClearFilter(definition.id)}
            />
          ))}
          <CrmTooltip content="Add filters" placement="bottom">
            <CrmButton
              iconOnly
              size="small"
              variant="tertiary"
              onClick={onAdvancedFilters}
              aria-label="Add filters"
            >
              <AddIcon />
            </CrmButton>
          </CrmTooltip>
          <button type="button" className="crm-list-advanced-link" onClick={onAdvancedFilters}>
            <TuneIcon />
            Advanced filters
          </button>
        </div>
      ) : null}

      {activeFilters.length ? (
        <div className="crm-list-active-filters" aria-label="Active filters">
          {activeFilters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className="crm-list-filter-chip"
              onClick={() => onClearFilter(filter.id)}
              aria-label={`Clear ${filter.definition?.label || filter.id} filter`}
            >
              <span>{filter.definition?.label || filter.id}</span>
              <strong>{getFilterSummary(filter, filter.definition, dateFormat)}</strong>
              <CloseIcon />
            </button>
          ))}
          <button type="button" className="crm-list-clear-all" onClick={onClearFilters}>Clear all</button>
        </div>
      ) : null}
    </div>
  );
}

export { CrmFilterPopover, CrmSortControl };
export default CrmListToolbar;
